import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ─── Types mirroring the database schema ────────────────────────────────────

export interface DBWarehouse {
  id: string;
  collection_id?: string | null;
  name: string;
  is_default: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface DBStorageArea {
  id: string;
  warehouse_id: string;
  name: string;
  is_default: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface DBOwner {
  id: string;
  collection_id?: string | null;
  name: string;
  notes: string | null;
  created_at: string;
}

export interface DBSupplier {
  id: string;
  collection_id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface DBItem {
  id: string;
  collection_id?: string | null;
  name: string;
  sku: string | null;
  category: string | null;
  notes: string | null;
  requires_serial?: boolean;
  requires_mac?: boolean;
  is_discontinued?: boolean;
  created_at: string;
  updated_at: string;
}

export interface DBStockRequest {
  id: string;
  collection_id: string;
  request_number: number;
  requester_id: string;
  warehouse_id: string;
  storage_area_id: string | null;
  priority: 'low' | 'medium' | 'high';
  notes: string | null;
  status: string;
  site_id: string | null;
  job_id: string | null;
  submitted_at: string | null;
  approved_at: string | null;
  reviewed_by: string | null;
  assigned_to: string | null;
  assignment_step: 'review' | 'order' | 'requester' | null;
  ordered_at: string | null;
  first_received_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface DBStockRequestLine {
  id: string;
  request_id: string;
  line_type: 'catalog' | 'free_text';
  item_id: string | null;
  free_text_description: string | null;
  substitute_item_id: string | null;
  substitute_status: 'pending_requester' | 'pending_manager' | 'accepted' | 'declined' | null;
  parent_line_id: string | null;
  dispute_reason: string | null;
  requested_qty: number;
  approved_qty: number | null;
  ordered_qty: number | null;
  ordered_at: string | null;
  received_qty: number;
  condition: 'new' | 'used';
  status: string;
  approval_reason: string | null;
  resolution_notes: string | null;
  po_reference: string | null;
  supplier: string | null;
  expected_date: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface DBStockRequestEvent {
  id: string;
  request_id: string;
  line_id: string | null;
  user_id: string;
  user_display_name: string;
  event_type: string;
  description: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface DBStockUnit {
  id: string;
  item_id: string;
  warehouse_id: string;
  storage_area_id: string | null;
  unit_id: number;
  serial_mac: string | null;
  condition: 'new' | 'used' | null;
  status: string;
  reference: string | null;
  owner: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface DBStockLog {
  id: string;
  stock_unit_id: string;
  event_type: string;
  description: string | null;
  reference: string | null;
  movement_type: string | null;
  from_warehouse_id: string | null;
  from_storage_area_id: string | null;
  to_warehouse_id: string | null;
  to_storage_area_id: string | null;
  /** Populated when auth is wired up; optional until then */
  acted_by?: string | null;
  created_at: string;
}

export interface DBCollection {
  id: string;
  name: string;
  customer: Record<string, unknown>;
  settings: Record<string, unknown>;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface DBDesign {
  id: string;
  collection_id: string;
  name: string;
  racks: unknown;
  equipment: unknown;
  cables: unknown;
  boxes: unknown;
  labels: unknown;
  floor_plan?: unknown | null;
  settings: Record<string, unknown>;
  legacy_design_id: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  client_write_id?: string | null;
  updated_by?: string | null;
}

export interface DBFloorplan {
  id: string;
  collection_id: string;
  name: string;
  settings: Record<string, unknown>;
  elements: unknown;
  width: number;
  height: number;
  overlay_path: string | null;
  legacy_id: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface DBSite {
  id: string;
  collection_id: string;
  name: string;
  location: string;
  tower_height: number;
  cross_member_width: number;
  section_height: number;
  tower_shape: 'square' | 'triangle';
  orientation_deg: number;
  stay_wires: unknown;
  network_designs: unknown;
  created_at: string;
  updated_at: string;
}

export interface DBSiteEquipment {
  id: string;
  site_id: string;
  equipment: unknown;
  mounting_height: number;
  mounting_side: string;
  azimuth: number;
  elevation: number;
  label: string | null;
  dish_size: number | null;
  equipment_offset: number | null;
  radio: unknown | null;
  radio_config: unknown | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface DBJob {
  id: string;
  site_id: string | null;
  collection_id: string | null;
  title: string;
  description: string | null;
  status: 'pending' | 'in-progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | null;
  assigned_to: string | null;
  due_date: string | null;
  recurrence_rule_id: string | null;
  occurrence_date: string | null;
  is_recurring_instance: boolean;
  created_at: string;
  updated_at: string;
}

export interface DBJobRecurrenceRule {
  id: string;
  site_id: string;
  title: string;
  description: string | null;
  priority: 'low' | 'medium' | 'high' | null;
  assigned_to: string | null;
  interval_count: number;
  interval_unit: 'days' | 'weeks' | 'months';
  start_date: string;
  end_date: string | null;
  next_run_at: string;
  last_run_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DBJobComment {
  id: string;
  job_id: string;
  text: string;
  author_name: string;
  created_at: string;
}

export interface DBJobHistory {
  id: string;
  job_id: string;
  field: string;
  from_value: string | null;
  to_value: string;
  changed_by: string;
  changed_at: string;
}

export interface ChatAttachment {
  name: string;
  path: string;
  url: string;
  size: number;
  mimeType: string;
}

export type ChatContextType = 'job' | 'stock_request' | 'recurrence_rule' | 'stock_request_line';

export interface DBChatMessage {
  id: string;
  context_type: ChatContextType;
  context_id: string;
  author_id: string | null;
  author_name: string;
  text: string;
  attachments: ChatAttachment[];
  created_at: string;
}
